/**
 * Collection/PLP "Notify Me" (Klaviyo Back in Stock) - vanilla JS, no jQuery.
 *
 * Submits directly to Klaviyo's public Back in Stock REST API so it does not
 * depend on Klaviyo's onsite.js detecting a "current product" - the
 * collection grid has many products at once, which onsite.js is not built to
 * scan (confirmed: it only auto-injects on single-product/PDP-shaped pages).
 * See https://developers.klaviyo.com/en/docs/how_to_set_up_custom_back_in_stock
 *
 * Markup contract (rendered by snippets/product-item-form.liquid and
 * snippets/klaviyo-notify-me-modal.liquid):
 *   [data-notify-me-button]        the trigger button on a product card,
 *                                  carries data-variant-id / data-product-id,
 *                                  and data-action="open-modal"
 *                                  aria-controls="modal-klaviyo-notify-me"
 *                                  so the theme's existing modal system
 *                                  (assets/theme.js _openModal/_closeModal)
 *                                  opens/closes it.
 *   #modal-klaviyo-notify-me       the shared modal, carries
 *                                  data-notify-me-company-id (Klaviyo public
 *                                  API key, from settings.klaviyo_notify_me_company_id)
 *                                  and data-notify-me-language (request.locale.iso_code)
 *   [data-notify-me-country-code]  dial-code <option>s each carry data-country
 *                                  (ISO 3166-1 alpha-2), combined with the
 *                                  language above into the $locale sent to
 *                                  Klaviyo's identify() on submit (e.g. "ar-KW")
 *   [data-notify-me-form]          the phone-number opt-in form inside it
 *
 * window.KlaviyoNotifyMe.updateCardButton(productItemEl, variantId, isAvailable)
 * is exposed so assets/theme.js's swatch-change handler can keep the trigger
 * in sync when a shopper switches color on a card without a page reload.
 */
(function () {
  'use strict';

  var API_URL = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var API_REVISION = '2024-06-15';
  var PHONE_PATTERN = /^\+[1-9]\d{6,14}$/;

  function getModal() {
    return document.getElementById('modal-klaviyo-notify-me');
  }

  function setModalState(modal, variantId) {
    var form = modal.querySelector('[data-notify-me-form]');
    var variantInput = modal.querySelector('[data-notify-me-variant-input]');
    var phoneInput = modal.querySelector('[data-notify-me-phone]');
    var errorEl = modal.querySelector('[data-notify-me-error]');
    var successEl = modal.querySelector('[data-notify-me-success]');
    var submitButton = modal.querySelector('[data-notify-me-submit]');

    if (variantInput) variantInput.value = variantId || '';
    if (phoneInput) phoneInput.value = '';
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    if (successEl) successEl.hidden = true;
    if (form) form.hidden = false;
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-busy');
    }
  }

  function showError(modal, message) {
    var errorEl = modal.querySelector('[data-notify-me-error]');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }

  function submitSubscription(companyId, variantId, phoneNumber) {
    var payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['WHATSAPP'],
          profile: {
            data: {
              type: 'profile',
              attributes: {
                phone_number: phoneNumber
              }
            }
          }
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: '$shopify:::$default:::' + variantId
            }
          }
        }
      }
    };

    return fetch(API_URL + '?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: API_REVISION
      },
      body: JSON.stringify(payload)
    });
  }

  // Capture phase: the theme's own modal-open delegate (assets/theme.js,
  // domDelegate bound to document.body) calls event.stopPropagation() for any
  // [data-action="open-modal"] trigger that isn't the quick-view button, which
  // stops the click from ever bubbling up to a document-level bubble listener.
  // Listening on the way down (capture) runs this before that happens, so the
  // variant id is always populated before the theme's own handler opens the
  // modal.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-notify-me-button]');
    if (!trigger) return;

    var modal = getModal();
    if (!modal) return;

    setModalState(modal, trigger.getAttribute('data-variant-id'));
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-notify-me-form]');
    if (!form) return;

    event.preventDefault();

    var modal = getModal();
    if (!modal) return;

    var companyId = modal.getAttribute('data-notify-me-company-id');
    var language = modal.getAttribute('data-notify-me-language');
    var variantInput = modal.querySelector('[data-notify-me-variant-input]');
    var countryCodeSelect = modal.querySelector('[data-notify-me-country-code]');
    var phoneInput = modal.querySelector('[data-notify-me-phone]');
    var submitButton = modal.querySelector('[data-notify-me-submit]');
    var successEl = modal.querySelector('[data-notify-me-success]');
    var errorEl = modal.querySelector('[data-notify-me-error]');

    var variantId = variantInput ? variantInput.value : '';
    var countryCode = countryCodeSelect ? countryCodeSelect.value : '';
    var countryDigits = countryCode.replace(/\D/g, '');

    // Local number only: strip everything but digits, and a leading trunk "0"
    // (e.g. "050 123 4567" -> "501234567"), since the country code above already
    // supplies that part of the E.164 string.
    var localNumber = phoneInput ? phoneInput.value.trim().replace(/\D/g, '') : '';
    // If the shopper typed the country code into the number field too (e.g.
    // pasted a full "+965 5012 3456" despite the select already being set to
    // Kuwait), drop the duplicate so it isn't counted twice.
    if (countryDigits && localNumber.indexOf(countryDigits) === 0) {
      localNumber = localNumber.slice(countryDigits.length);
    }
    localNumber = localNumber.replace(/^0+/, '');
    var rawPhone = '+' + countryDigits + localNumber;

    if (errorEl) errorEl.hidden = true;

    if (!companyId || !variantId) {
      // Distinct from the phone-format error below: this means the modal was
      // opened without a variant id being recorded (see the capture-phase
      // click listener above) rather than anything the shopper typed.
      showError(modal, window.languages && window.languages.klaviyoNotifyMeError ? window.languages.klaviyoNotifyMeError : 'Something went wrong. Please try again.');
      return;
    }

    if (!PHONE_PATTERN.test(rawPhone)) {
      showError(modal, window.languages && window.languages.klaviyoNotifyMeInvalidPhone ? window.languages.klaviyoNotifyMeInvalidPhone : 'Please enter a valid phone number, including country code.');
      return;
    }

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
    }

    // Best-effort: tag the profile's reserved $locale field (language from the
    // page, country from the dial code the shopper actually picked - the same
    // pairing Klaviyo's own segments key off, e.g. "ar-KW"). Independent of
    // the back-in-stock subscription call below, which has no field for this
    // (see submitSubscription/API docs) - a failure here must never block it.

    submitSubscription(companyId, variantId, rawPhone)
      .then(function (response) {
        if (!response.ok && response.status !== 202) {
          throw new Error('Klaviyo back-in-stock subscription failed with status ' + response.status);
        }
        form.hidden = true;
        if (successEl) successEl.hidden = false;
      })
      .catch(function (error) {
        if (window.console && window.console.error) {
          window.console.error('[klaviyo-notify-me]', error);
        }
        showError(modal, window.languages && window.languages.klaviyoNotifyMeError ? window.languages.klaviyoNotifyMeError : 'Something went wrong. Please try again.');
      })
      .finally(function () {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.removeAttribute('aria-busy');
        }
      });
  });

  window.KlaviyoNotifyMe = {
    updateCardButton: function (productItemEl, variantId, isAvailable) {
      if (!productItemEl) return;

      var notifyButton = productItemEl.querySelector('.js-notify-me-button');
      var cartButton = productItemEl.querySelector('.ProductForm__AddToCart');
      var buttonsWrapper = productItemEl.querySelector('.product-item-grid-buttons-wrapper');

      if (!notifyButton) return;

      if (isAvailable) {
        notifyButton.hidden = true;
        if (buttonsWrapper) buttonsWrapper.classList.remove('product-item-grid-buttons-wrapper--notify-me');
      } else {
        notifyButton.setAttribute('data-variant-id', variantId);
        notifyButton.hidden = false;
        if (cartButton) cartButton.hidden = true;
        // Mobile: hides the "Sold Out" Quick View bar so only Notify Me shows.
        if (buttonsWrapper) buttonsWrapper.classList.add('product-item-grid-buttons-wrapper--notify-me');
      }
    }
  };
})();
